import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, IconButton, Menu, MenuItem, Chip, Alert,
  Skeleton, alpha, useTheme,
  ListItemIcon, ListItemText, Tabs, Tab, Tooltip, CircularProgress,
  Checkbox, Link,
} from '@mui/material';
import {
  Search, MoreVert, ReceiptLong, CalendarMonth, Cancel, Visibility,
  Download as DownloadIcon, Timer, Close as CloseIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeInvoiceService from '../../services/feeInvoiceService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ReasonDialog from '../../components/common/ReasonDialog';
import SearchableSelect from '../../components/common/SearchableSelect';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import { buildSessionOptions } from '../../utils/sessionOptions';
import GenerateInvoicesDialog from '../../components/fees/GenerateInvoicesDialog';
import InvoiceDetailDialog from '../../components/fees/InvoiceDetailDialog';
import { useFeeVoucherPdf } from '../../components/fees/useFeeVoucherPdf';
import PdfRenderingDialog from '../../components/common/PdfRenderingDialog';
import { useSchoolBranding } from '../../utils/pdf';
import ApplyLateFeesDialog from '../../components/fees/ApplyLateFeesDialog';
import lateFeePolicyService from '../../services/lateFeePolicyService';
import { getHashedColor } from '../../utils/colorUtils';

import {
  FeeInvoiceRow, FeeInvoiceStatus, FeeInvoiceView,
} from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { formatChallanNumber } from '../../utils/challanNumber';
import ChallanNumberCell from '../../components/fees/ChallanNumberCell';
import PayerCell from '../../components/fees/PayerCell';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const DEFAULT_ROWS_PER_PAGE = 25;

const STATUS_LABEL: Record<FeeInvoiceStatus, string> = {
  issued:    'Issued',
  partial:   'Partial',
  paid:      'Paid',
  waived:    'Waived',
  cancelled: 'Cancelled',
};

/** Shown as a chip beside the challan number when the invoice is NOT regular
 *  tuition — so operators can tell admission-fee rows apart at a glance. */
const KIND_LABEL: Record<string, string> = {
  application_fee: 'Application fee',
  admission_fee:   'Admission fee',
  other:           'Other',
};

const VIEW_LABEL: Record<FeeInvoiceView, string> = {
  all:         'All',
  outstanding: 'Outstanding',
  overdue:     'Overdue',
  paid:        'Paid',
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Convert a "YYYY-MM" HTML month input to the canonical period label used by
 * the invoice model ("MMM YYYY"). Server matches on `period.label` exactly, so
 * this must mirror `resolvePeriod` in `invoiceGenerationService`.
 */
const monthToPeriodLabel = (m: string): string | undefined => {
  const match = /^(\d{4})-(\d{2})$/.exec(m);
  if (!match) return undefined;
  const idx = Number(match[2]) - 1;
  if (idx < 0 || idx > 11) return undefined;
  return `${MONTH_ABBR[idx]} ${match[1]}`;
};

const FeeInvoicesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fees');

  const urlSession = searchParams.get('sessionId') ?? '';
  const urlClass   = searchParams.get('classId') ?? '';
  const urlMonth   = searchParams.get('month') ?? '';
  const urlView    = (searchParams.get('view') as FeeInvoiceView) || 'outstanding';
  const urlSearch  = searchParams.get('search') ?? '';
  const urlGrNumber = searchParams.get('grNumber') ?? '';
  const urlPage    = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [sessionId, setSessionId] = useState(urlSession);
  const [classId, setClassId] = useState(urlClass);
  const [month, setMonth] = useState(urlMonth);
  const [view, setView] = useState<FeeInvoiceView>(urlView);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const search = useDebounce(searchInput, 300);
  const [grNumberInput, setGrNumberInput] = useState(urlGrNumber);
  const grNumber = useDebounce(grNumberInput, 300);
  const [page, setPage] = useState(urlPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [rows, setRows] = useState<FeeInvoiceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({ net: 0, collected: 0, outstanding: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  // ─── Bulk selection ───────────────────────────────────────────────────
  // Cap mirrors the backend so the FE never lets you commit a selection
  // the server would reject.
  const BULK_DOWNLOAD_CAP = 200;
  // Explicit-selection mode: `_id`s the bursar toggled by hand. Survives
  // pagination and filter changes so the user can build a batch across
  // pages. Cleared explicitly (button or successful download).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  // Skeleton only on the initial mount. Subsequent loads (search, filter,
  // pagination) keep the old rows visible so the table feels tight — no
  // flash-to-empty when the user types.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  // Dialogs
  const [generateOpen, setGenerateOpen] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<FeeInvoiceRow | null>(null);
  const [lateFeesOpen, setLateFeesOpen] = useState(false);
  const [lateFeePolicyEnabled, setLateFeePolicyEnabled] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<FeeInvoiceRow | null>(null);

  // URL sync
  useEffect(() => {
    const next = new URLSearchParams();
    if (sessionId) next.set('sessionId', sessionId);
    if (classId)   next.set('classId', classId);
    if (month)     next.set('month', month);
    if (view !== 'outstanding') next.set('view', view);
    if (search)    next.set('search', search);
    if (grNumber) next.set('grNumber', grNumber);
    if (page > 0)  next.set('page', String(page + 1));
    setSearchParams(next, { replace: true });
  }, [sessionId, classId, month, view, search, grNumber, page, setSearchParams]);

  // Load pickers + policy flag
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls, policy] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
          lateFeePolicyService.getLateFeePolicy().catch(() => null),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        setLateFeePolicyEnabled(Boolean(policy?.enabled));
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Bulk selection derived state + handlers ─────────────────────────────

  const visibleSelectedCount = useMemo(
    () => rows.filter(r => selectedIds.has(r._id)).length,
    [rows, selectedIds],
  );
  const isAllVisibleSelected = rows.length > 0 && visibleSelectedCount === rows.length;
  const isSomeVisibleSelected = visibleSelectedCount > 0 && !isAllVisibleSelected;
  // "Select all N matching filter" is only offered when there are more
  // matches than the current page shows AND we haven't already expanded.
  const canExpandToAllMatching = isAllVisibleSelected
    && !selectAllMatching
    && total > rows.length;
  // If total > cap, expanding is disallowed at the click level too — the
  // link renders disabled with a tooltip so the user sees the reason.
  const overCapForFilter = total > BULK_DOWNLOAD_CAP;

  const toggleRow = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Unchecking anything in filter-expanded mode drops back to explicit
    // mode — the user's deselect is their intent, don't fight it (Gmail
    // behaviour). Selected IDs stay intact; the label just changes.
    if (selectAllMatching) setSelectAllMatching(false);
  }, [selectAllMatching]);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (isAllVisibleSelected) {
        rows.forEach(r => next.delete(r._id));
      } else {
        rows.forEach(r => next.add(r._id));
      }
      return next;
    });
    if (selectAllMatching) setSelectAllMatching(false);
  }, [rows, isAllVisibleSelected, selectAllMatching]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, []);

  const activateSelectAllMatching = useCallback(async () => {
    if (total === 0 || overCapForFilter) return;
    try {
      const res = await feeInvoiceService.listFeeInvoices({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        periodLabel: monthToPeriodLabel(month),
        view,
        search: search || undefined,
        grNumber: grNumber || undefined,
        page: 1,
        limit: BULK_DOWNLOAD_CAP,
      });
      setSelectedIds(new Set(res.invoices.map(i => i._id)));
      setSelectAllMatching(true);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to expand selection', { variant: 'error' });
    }
  }, [total, overCapForFilter, sessionId, classId, month, view, search, grNumber, enqueueSnackbar]);

  // Filter changes invalidate a "select all matching" snapshot — the
  // snapshot was for the OLD filter. Explicit picks stay intact.
  useEffect(() => {
    if (selectAllMatching) setSelectAllMatching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, classId, month, view, search, grNumber]);

  // Vouchers are drawn in the browser: the pack shows a progress dialog, a row its own spinner.
  const { branding } = useSchoolBranding();
  const vouchers = useFeeVoucherPdf(branding);
  const bulkDownloading = vouchers.isBusy('pack');
  const bulkDownload = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || ids.length > BULK_DOWNLOAD_CAP) return;
    if (await vouchers.download('pack', () => feeInvoiceService.printInvoices(ids), ids.length)) clearSelection();
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feeInvoiceService.listFeeInvoices({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        periodLabel: monthToPeriodLabel(month),
        view,
        search: search || undefined,
        grNumber: grNumber || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(res.invoices);
      setTotal(res.pagination.totalItems);
      setKpis(res.kpis);
      setHasLoadedOnce(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load challans', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, month, view, search, grNumber, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: FeeInvoiceRow) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const openDetail = (row: FeeInvoiceRow) => {
    setDetailInvoiceId(row._id);
    closeMenu();
  };

  const downloadPdf = (row: FeeInvoiceRow) => {
    closeMenu();
    if (!vouchers.isBusy(row._id)) vouchers.download(row._id, () => feeInvoiceService.getInvoicePrint(row._id));
  };

  const openCancel = (row: FeeInvoiceRow) => {
    setCancelTarget(row);
    closeMenu();
  };

  // ReasonDialog shows a failure inline and keeps itself open.
  const confirmCancel = async (reason: string) => {
    if (!cancelTarget) return;
    await feeInvoiceService.cancelInvoice(cancelTarget._id, reason);
    enqueueSnackbar(`Cancelled ${formatChallanNumber(cancelTarget.invoiceNumber)}`, { variant: 'success' });
    load();
  };

  const currencyFmt = useCurrencyFormat();

  const resetFilters = () => {
    setClassId('');
    setMonth('');
    setView('outstanding');
    setSearchInput('');
    setGrNumberInput('');
    setPage(0);
  };

  const skeletonRows = useMemo(() =>
    Array.from({ length: 6 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} /></TableCell>
        <TableCell><Skeleton variant="text" width={100} /></TableCell>
        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
        <TableCell><Skeleton variant="text" width="50%" /></TableCell>
        <TableCell><Skeleton variant="text" width={60} /></TableCell>
        <TableCell><Skeleton variant="text" width={70} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
        <TableCell><Skeleton variant="text" width={60} /></TableCell>
        <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
      </TableRow>
    )),
    []);

  if (!canReadFees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">You don't have permission to view fee challans.</Alert>
      </Box>
    );
  }

  const statusStyle = (row: FeeInvoiceRow) => {
    if (row.status === 'cancelled') {
      return {
        borderColor: alpha(theme.palette.text.primary, 0.16),
        color: 'text.disabled',
        bgcolor: 'transparent',
      };
    }
    if (row.isOverdue) {
      return {
        borderColor: alpha(theme.palette.error.main, 0.34),
        color: theme.palette.error.dark,
        bgcolor: alpha(theme.palette.error.main, 0.08),
      };
    }
    if (row.status === 'paid' || row.status === 'waived') {
      return {
        borderColor: alpha(theme.palette.success.main, 0.32),
        color: theme.palette.success.dark,
        bgcolor: alpha(theme.palette.success.main, 0.08),
      };
    }
    if (row.status === 'partial') {
      return {
        borderColor: alpha(theme.palette.warning.main, 0.32),
        color: theme.palette.warning.dark,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
      };
    }
    return {
      borderColor: alpha(theme.palette.info.main, 0.3),
      color: theme.palette.info.dark,
      bgcolor: alpha(theme.palette.info.main, 0.06),
    };
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Fee Challans</Typography>
          <Typography variant="body2" color="text.secondary">
            Materialized bills per assignment per period. Generate for a month, then collect payments as they come in.
          </Typography>
        </Box>
        {canWriteFees && (
          <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-start' }}>
            {lateFeePolicyEnabled && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Timer />}
                onClick={() => setLateFeesOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                Apply late fees
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              startIcon={<CalendarMonth />}
              onClick={() => setGenerateOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              Generate for period
            </Button>
          </Stack>
        )}
      </Stack>

      {/* KPIs */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Kpi label="Challans" value={String(kpis.count)} theme={theme} />
        <Kpi label="Net billed" value={currencyFmt(kpis.net)} theme={theme} />
        <Kpi label="Collected" value={currencyFmt(kpis.collected)} theme={theme} accent="success" />
        <Kpi label="Outstanding" value={currencyFmt(kpis.outstanding)} theme={theme} accent="warning" />
      </Stack>

      {/* View tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs
          value={view}
          onChange={(_, v) => { setView(v as FeeInvoiceView); setPage(0); }}
          sx={{ px: 1, '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}
        >
          {(Object.keys(VIEW_LABEL) as FeeInvoiceView[]).map(k => (
            <Tab key={k} value={k} label={VIEW_LABEL[k]} />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect
            label="Session"
            value={sessionId}
            onChange={v => { setSessionId(v); setPage(0); }}
            options={buildSessionOptions(sessions, theme)}
            placeholder="All sessions"
            minWidth={200}
          />
          <SearchableSelect
            label="Class"
            value={classId}
            onChange={v => { setClassId(v); setPage(0); }}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="All classes"
            minWidth={200}
          />
          <TextField
            size="small"
            label="Month"
            type="month"
            value={month}
            onChange={e => { setMonth(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            placeholder="Search by student name or challan #…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            label="GR / application no."
            placeholder="000042 / APP-…"
            value={grNumberInput}
            onChange={e => {
              // A GR number is digits only (a paste with spaces still maps to
              // it); input starting with a letter is an applicant's "APP-…"
              // number, kept as typed (from the first letter, so it can be typed).
              const raw = e.target.value;
              const v = /^\s*[a-z]/i.test(raw)
                ? raw.trim().toUpperCase().slice(0, 14)
                : raw.replace(/\D/g, '').slice(0, 6);
              setGrNumberInput(v);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
            inputProps={{ maxLength: 14 }}
            sx={{ width: 220 }}
          />
          {(classId || month || view !== 'outstanding' || search || grNumber) && (
            <Button size="small" onClick={resetFilters}>Reset</Button>
          )}
        </Stack>
      </Paper>

      {/* Bulk selection action bar — appears above the table when N ≥ 1. */}
      {selectedIds.size > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.5, sm: 1.75 },
            borderRadius: 2,
            mb: 2,
            borderColor: alpha(theme.palette.primary.main, 0.35),
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} sx={{ color: theme.palette.primary.dark }}>
                {selectAllMatching
                  ? `All ${selectedIds.size} challan${selectedIds.size === 1 ? '' : 's'} matching your filter selected`
                  : `${selectedIds.size} challan${selectedIds.size === 1 ? '' : 's'} selected`}
              </Typography>
              {selectAllMatching && (
                <Chip
                  size="small"
                  label="Filter-wide"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 600, letterSpacing: 0.3,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.dark,
                  }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <Button
                size="small"
                onClick={clearSelection}
                startIcon={<CloseIcon fontSize="small" />}
                sx={{ color: 'text.secondary' }}
                disabled={bulkDownloading}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={bulkDownload}
                disabled={bulkDownloading || selectedIds.size === 0}
                startIcon={
                  bulkDownloading
                    ? <CircularProgress size={14} thickness={5} color="inherit" />
                    : <DownloadIcon fontSize="small" />
                }
                sx={{ borderRadius: 1.5, minWidth: 160, fontWeight: 700 }}
              >
                {bulkDownloading
                  ? 'Preparing…'
                  : `Download PDF (${selectedIds.size})`}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Select-all-matching banner — Gmail-style two-step. Shows only when
            the user has just filled the visible page's selection AND more
            matches exist beyond the visible page. */}
        {canExpandToAllMatching && (
          <Box
            sx={{
              px: 2, py: 1.25,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2">
              <strong>All {visibleSelectedCount} on this page selected.</strong>{' '}
              {overCapForFilter ? (
                <Tooltip title={`${total} match — max ${BULK_DOWNLOAD_CAP} per PDF. Narrow the filter to enable.`} arrow>
                  <span style={{ color: theme.palette.text.disabled }}>
                    Select all {total} matching your filter
                  </span>
                </Tooltip>
              ) : (
                <Link
                  component="button"
                  type="button"
                  onClick={activateSelectAllMatching}
                  underline="hover"
                  sx={{ fontWeight: 700, color: theme.palette.primary.main }}
                >
                  Select all {total} matching your filter →
                </Link>
              )}
            </Typography>
          </Box>
        )}

        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={isAllVisibleSelected}
                  indeterminate={isSomeVisibleSelected}
                  onChange={toggleAllVisible}
                  disabled={rows.length === 0}
                  inputProps={{ 'aria-label': 'Select all visible challans' }}
                />
              </TableCell>
              <TableCell>Challan</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Cohort</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Due</TableCell>
              <TableCell align="right">Outstanding</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ pr: 2 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasLoadedOnce ? (
              skeletonRows
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <ReceiptLong sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    No challans to show
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {canWriteFees ? 'Generate challans for a period to start billing.' : 'Ask an admin to generate challans.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                const sectionColor = row.section?.name
                  ? getHashedColor(String(row.section._id || row.section.name), theme.palette.mode === 'dark')
                  : null;
                const isSelected = selectedIds.has(row._id);
                return (
                  <TableRow
                    key={row._id}
                    hover
                    onClick={() => openDetail(row)}
                    selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected, &.Mui-selected:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleRow(row._id)}
                        inputProps={{ 'aria-label': `Select challan ${row.invoiceNumber}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <ChallanNumberCell value={row.invoiceNumber} fontWeight={700} />
                        {row.kind && row.kind !== 'tuition' && (
                          <Chip
                            size="small"
                            label={KIND_LABEL[row.kind] || row.kind}
                            variant="outlined"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              borderColor: alpha(theme.palette.info.main, 0.32),
                              color: theme.palette.info.dark,
                              bgcolor: alpha(theme.palette.info.main, 0.08),
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {row.lineItemCount} line{row.lineItemCount === 1 ? '' : 's'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <PayerCell payer={row.student} rollNumber={row.rollNumber} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography variant="body2" fontWeight={600}>{row.class?.name ?? '—'}</Typography>
                        {row.section?.name && sectionColor && (
                          <Chip
                            label={row.section.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20, fontSize: '0.68rem', fontWeight: 600, letterSpacing: 0.2,
                              borderColor: alpha(sectionColor.text, 0.35),
                              color: sectionColor.text,
                              bgcolor: sectionColor.bg,
                              '& .MuiChip-label': { px: 0.9 },
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.period.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{format(new Date(row.dueDate), 'MMM d, yyyy')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          color: row.totals.outstanding > 0 ? theme.palette.warning.dark : 'text.disabled',
                        }}
                      >
                        {currencyFmt(row.totals.outstanding)}
                      </Typography>
                      {row.totals.paid > 0 && row.totals.outstanding > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          of {currencyFmt(row.totals.net)}
                        </Typography>
                      )}
                      {row.arrearsTotal != null && row.arrearsTotal > 0 && row.status !== 'paid' && row.status !== 'cancelled' && row.status !== 'waived' && (
                        <Tooltip
                          title={`${row.arrearsCount} prior unpaid challan${row.arrearsCount === 1 ? '' : 's'} — ${currencyFmt(row.arrearsTotal)} will print on this voucher as arrears.`}
                          arrow
                          placement="top"
                        >
                          <Chip
                            size="small"
                            label={`+ ${currencyFmt(row.arrearsTotal)} arrears`}
                            sx={{
                              height: 18, fontSize: '0.65rem', fontWeight: 600, mt: 0.5,
                              bgcolor: alpha(theme.palette.warning.main, 0.14),
                              color: theme.palette.warning.dark,
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.32)}`,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isOverdue && row.status !== 'paid' ? 'Overdue' : STATUS_LABEL[row.status]}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, ...statusStyle(row) }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1 }} onClick={e => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title={vouchers.isBusy(row._id) ? 'Preparing PDF…' : 'Download voucher (PDF)'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => downloadPdf(row)}
                              disabled={vouchers.isBusy(row._id)}
                              aria-label="Download voucher"
                            >
                              {vouchers.isBusy(row._id)
                                ? <CircularProgress size={16} thickness={5} />
                                : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton size="small" onClick={e => openMenu(e, row)} aria-label="Row actions">
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuWithHintsProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => menuRow && openDetail(menuRow)}>
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText primary="View details" />
        </MenuItem>
        <MenuItem onClick={() => menuRow && downloadPdf(menuRow)}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Download voucher (PDF)" />
        </MenuItem>
        {canWriteFees && menuRow && menuRow.status !== 'cancelled' && menuRow.totals.paid === 0 && (
          <MenuItem onClick={() => menuRow && openCancel(menuRow)} sx={{ color: 'error.main' }}>
            <ListItemIcon><Cancel fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Cancel challan" secondary="Only unpaid challans" />
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <GenerateInvoicesDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={() => { load(); }}
        defaultSessionId={sessionId || undefined}
        defaultClassId={classId || undefined}
      />

      <ApplyLateFeesDialog
        open={lateFeesOpen}
        onClose={() => setLateFeesOpen(false)}
        onApplied={() => { load(); }}
        defaultSessionId={sessionId || undefined}
        defaultClassId={classId || undefined}
      />

      <PdfRenderingDialog open={vouchers.packing > 0} label={`Preparing ${vouchers.packing} voucher${vouchers.packing === 1 ? '' : 's'}`} hint="Large packs take a few seconds." />

      <InvoiceDetailDialog
        open={Boolean(detailInvoiceId)}
        invoiceId={detailInvoiceId}
        onClose={() => setDetailInvoiceId(null)}
        onChanged={load}
      />

      <ReasonDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        title="Cancel this challan?"
        facts={cancelTarget ? [
          { label: 'Challan', value: formatChallanNumber(cancelTarget.invoiceNumber) },
          { label: 'Student', value: cancelTarget.student?.name ?? '—' },
        ] : []}
        consequence="The family no longer owes this challan and the charge is reversed in the books. A fresh challan can be generated for the same month later."
        confirmText="Cancel challan"
        reasonPlaceholder="e.g. Issued with the wrong fee plan"
        onConfirm={confirmCancel}
      />
    </Box>
  );
};

const Kpi: React.FC<{ label: string; value: string; theme: any; accent?: 'success' | 'warning' }> = ({ label, value, theme, accent }) => {
  const color = accent === 'success' ? theme.palette.success.dark
              : accent === 'warning' ? theme.palette.warning.dark
              : theme.palette.text.primary;
  const bg = accent === 'success' ? alpha(theme.palette.success.main, 0.06)
           : accent === 'warning' ? alpha(theme.palette.warning.main, 0.06)
           : alpha(theme.palette.text.primary, 0.02);
  const border = accent === 'success' ? alpha(theme.palette.success.main, 0.2)
              : accent === 'warning' ? alpha(theme.palette.warning.main, 0.2)
              : theme.palette.divider;
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, flex: 1, bgcolor: bg, borderColor: border }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Paper>
  );
};

export default FeeInvoicesPage;
